import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'
import { okSchema } from './vendors'

const bannerLocsSchema = z.object({ excludedLocs: z.array(z.string()), allowedLocs: z.array(z.string()) })

// GET /v1/admin/locations/config -> { success, data: { dtlr, spc } }
const configEnvelopeSchema = z.object({
  success: z.boolean(),
  data: z.object({ dtlr: bannerLocsSchema, spc: bannerLocsSchema }),
})

export type LocationFilterBanner = 'dtlr' | 'spc'
export type BannerLocationFilters = z.infer<typeof bannerLocsSchema>
export type AdminLocationsConfig = z.infer<typeof configEnvelopeSchema>['data']

export const adminLocationsConfigQueryKey = ['admin', 'locations-config'] as const

/** Pure: normalize chip-input text (comma/space/newline separated) to a de-duplicated list of store codes. */
export function parseLocCodes(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ]
}

/** Per-banner include/exclude location filters. */
export function useAdminLocationsConfig() {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'admin-locations'] : adminLocationsConfigQueryKey,
    queryFn: async (): Promise<AdminLocationsConfig> => {
      const res = await api.get('/v1/admin/locations/config', configEnvelopeSchema)
      if (!res.success) throw new Error('Failed to fetch location config')
      return res.data
    },
    enabled: !preview,
    ...(preview ? { initialData: preview.adminLocationsConfig, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

/**
 * Save one field per call. `mutate({ banner, excludedLocs })` or `mutate({ banner, allowedLocs })`
 * (at least one required) PATCH /v1/admin/locations/config. Invalidates the config AND the locations list.
 */
export function useUpdateLocationsConfig() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: { banner: LocationFilterBanner; excludedLocs?: string[]; allowedLocs?: string[] }) => {
      if (preview) return { success: true }
      if (v.excludedLocs === undefined && v.allowedLocs === undefined) {
        throw new Error('Provide excludedLocs or allowedLocs')
      }
      return api.request('/v1/admin/locations/config', { method: 'PATCH', body: v }, okSchema)
    },
    onSuccess: async () => {
      if (preview) return
      await Promise.all([
        qc.invalidateQueries({ queryKey: adminLocationsConfigQueryKey }),
        qc.invalidateQueries({ queryKey: ['locations'] }),
      ])
    },
  })
}
